// Builds the recruitment application email from the collected form state.
//
// Applications previously went to a Google Apps Script web app whose deployment
// no longer exists (the /exec URL returns 404), and the failure was swallowed —
// which is why applicants saw no acknowledgement and the submissions were lost.
// They now go through the same Brevo path as the rest of the site.
import { escapeHtml } from "../../../services/email";
import { stepOnePartOne, stepOnePartTwo, stepOnePartThree } from "./step1Data";
import { stepTwo } from "./step2Data";
import { stepThree } from "./step3Data";
import { stepFour } from "./step4Data";
import { stepFive } from "./step5Data";
import { stepSix } from "./step6Data";

const SECTIONS = [
  { title: "Personal Details", items: [...stepOnePartOne, ...stepOnePartTwo, ...stepOnePartThree] },
  { title: "Educational Background", items: stepTwo },
  { title: "Interests", items: stepThree },
  { title: "Skill Assessment", items: stepFour },
  { title: "Situations", items: stepFive },
  { title: "Feedback", items: stepSix },
];

const row = (label, value) => `
  <tr>
    <td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;font-size:13px;color:#6b7280;width:45%;vertical-align:top;">${escapeHtml(
      label
    )}</td>
    <td style="padding:10px 16px;border-bottom:1px solid #e5e7eb;font-size:14px;color:#111827;white-space:pre-wrap;">${escapeHtml(
      value
    )}</td>
  </tr>`;

const section = (title, rows) => `
  <h2 style="margin:28px 0 10px;font-size:15px;color:#1b7840;text-transform:uppercase;letter-spacing:0.6px;">${escapeHtml(
    title
  )}</h2>
  <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:8px;border-collapse:separate;overflow:hidden;">
    ${rows}
  </table>`;

export function buildApplicationEmail(data) {
  const answered = new Set();

  const body = SECTIONS.map(({ title, items }) => {
    const rows = items
      .filter((item) => {
        const value = data[item.stateName];
        return value !== undefined && value !== null && String(value).trim() !== "";
      })
      .map((item) => {
        answered.add(item.stateName);
        return row(item.fieldLabel, data[item.stateName]);
      })
      .join("");
    return rows ? section(title, rows) : "";
  }).join("");

  // Anything captured in form state but not described by the step data.
  const extraRows = Object.entries(data)
    .filter(([key, value]) => !answered.has(key) && String(value ?? "").trim() !== "")
    .map(([key, value]) => row(key, value))
    .join("");

  const applicantName = data.fullName || "Applicant";

  return {
    subject: `New Recruitment Application — ${applicantName}`,
    htmlContent: `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width, initial-scale=1.0"/></head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 0;">
    <tr><td align="center">
      <table width="640" cellpadding="0" cellspacing="0" style="max-width:640px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
        <tr><td style="background-color:#1b7840;padding:32px 40px;text-align:center;">
          <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;">Terces Solutions</h1>
          <p style="margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:13px;">Recruitment Application</p>
        </td></tr>
        <tr><td style="padding:32px 40px;">
          <p style="margin:0;font-size:15px;color:#374151;">A new application was submitted by <strong>${escapeHtml(
            applicantName
          )}</strong>.</p>
          ${body}
          ${extraRows ? section("Other Answers", extraRows) : ""}
        </td></tr>
        <tr><td style="background-color:#f9fafb;border-top:1px solid #e5e7eb;padding:24px 40px;text-align:center;">
          <p style="margin:0;font-size:12px;color:#9ca3af;">Submitted through the recruitment form at <strong>terces.io</strong></p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`,
  };
}
